import React from 'react';
import WideView from 'src/components/atomic/layouts/wideView/WideView';
import Card from 'src/components/atomic/atoms/card/Card';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import styles from './BudgetSettingsView.module.scss';
import LiteralsService from 'src/services/literals/LiteralsService';
import BudgetAccounts from 'src/components/atomic/molecules/budgetAccounts/BudgetAccounts';
import PlaidLinkAccountsCallout from 'src/components/atomic/molecules/plaidLinkAccountsCallout/PlaidLinkAccountsCallout';
import { useAllAccounts } from 'src/hooks/queries/accounts';
import PlaidConnector from 'src/components/atomic/atoms/plaidConnector/PlaidConnector';
import { useSpendSmartDocumentTitle } from 'src/hooks/useDocumentTitle';
import { useHistory } from 'react-router-dom';
import { IconWarning } from '@americanexpress/dls-icons';
import { SyncStatus } from 'src/types/institution.model';
import { AccountModel } from 'src/types/account.model';
import { trackClick, useTrackPage } from 'src/hooks/useAdobeTags';
import Unenroll from 'src/components/atomic/atoms/unenroll/Unenroll';
import { ButtonSecondary } from '@americanexpress/dls-react';
import budgetStyles from 'src/components/atomic/pages/budgets/budgetStyles.module.scss';
import Selectors from 'src/utils/selectors';
import Divider from 'src/components/atomic/atoms/divider/Divider';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import adobeTags from 'src/types/adobe-tags';
import { goBack } from 'src/services/router.service';
import ManageEmailPreferences from 'src/components/atomic/organisms/manageEmailPreferences/ManageEmailPreferences';
import { useFeatureFlag } from 'src/hooks/queries/one-xp';
import oneXP from 'src/types/one-xp';

export default function BudgetSettingsView(): JSX.Element {
    useSpendSmartDocumentTitle(LiteralsService.getLiteral('Budget.titles.settings'));
    useTrackPage(LiteralsService.getLiteral('Budget.titles.settings'));
    const history = useHistory();
    const { isLoading, data: accounts } = useAllAccounts();
    const { isMinWidthMedium } = useBreakpoints();
    const { data: manageEmailPreferencesFeatureFlag } = useFeatureFlag(oneXP.FeatureFlags.ManageEmailPreferences);

    // Double loader causes janky behavior
    if (isLoading) return <div />;

    const disconnectedAccounts: AccountModel[] = accounts?.filter(
        (e) => e.institution.syncStatus === SyncStatus.LoginRequired
    );
    const disconnectedInstitutionIds: Set<string> = new Set(
        disconnectedAccounts?.map((e) => e.institution.institutionId)
    );

    return (
        <WideView
            title={LiteralsService.getLiteral('Budget.Settings.title')}
            hasBackgroundBanner
            showFooter
            showTermsAndConditions
            hideBackButton
            history={history}
            withDocTitle={false}
            preTitle={
                disconnectedAccounts?.length > 0 && (
                    <div className={styles.alert}>
                        <IconWarning />
                        <Typography variant={TypographyVariants.BODY} component="p">
                            {disconnectedInstitutionIds.size === 1
                                ? `${disconnectedAccounts[0].institution.name}${LiteralsService.getLiteral(
                                      'Budget.Settings.isDisconnected'
                                  )}`
                                : `${disconnectedInstitutionIds.size}${LiteralsService.getLiteral(
                                      'Budget.Settings.institutionsAreDisconnected'
                                  )}`}
                            <PlaidConnector banner anchor>
                                {LiteralsService.getLiteral('Budget.Settings.reconnectNow')}
                            </PlaidConnector>
                        </Typography>
                    </div>
                )
            }
            data-cy={Selectors.SpendSmart.SETTINGS_ROOT}
        >
            <Card className={budgetStyles.cardBody} showBackgroundBanner>
                <div className={styles.columns}>
                    <BudgetAccounts />
                    {!isMinWidthMedium && <Divider />}
                    <PlaidLinkAccountsCallout />
                </div>
                {manageEmailPreferencesFeatureFlag && (
                    <>
                        <Divider color="weak" />
                        <div className={styles.emailPreferences}>
                            <ManageEmailPreferences />
                        </div>
                    </>
                )}
                <Divider color="weak" />
                <Unenroll className={styles.unenroll} />
                <div className={`${budgetStyles.budgetBottomButtons}`}>
                    <ButtonSecondary
                        onClick={() => {
                            trackClick(location.pathname, adobeTags.Bottom.Back);
                            goBack();
                        }}
                    >
                        {LiteralsService.getLiteral('CommonText.back')}
                    </ButtonSecondary>
                </div>
            </Card>
        </WideView>
    );
}
